import React, { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { Container, Row, Col, Breadcrumb, Pagination as BootstrapPagination } from 'react-bootstrap'
import Lightbox from 'react-18-image-lightbox'
import Masonry from 'react-masonry-css'
import 'react-18-image-lightbox/style.css'

// Functions
import { fetchPostById, fetchPostsByType } from '../../Functions/apiService'
import { formatDate, getImageUrl, getPostTypeUrl } from '../../Functions/Utilities'
import { resolveCreator, resolvePostTypeName, inferRouteSegmentFromPath, getNationalityFontClass } from '../../Functions/imigoWebConfig'

// Context
import { useLanguage } from '../../Context/LanguageContext'

// Components
import PostCard from '../../Components/Imigo/PostCard'
import InstallAppCTA from '../../Components/Imigo/InstallAppCTA'
import SEO from '../../Components/SEO/SEO'

// Custom API function to fetch products with pagination
const fetchBusinessProducts = async (businessIdOrSlug, page = 1, limit = 8) => {
  try {
    // Check if the parameter is a number (ID) or string (slug)
    const isId = !isNaN(parseInt(businessIdOrSlug));
    
    let queryParam = isId 
      ? `filters[post][id]=${businessIdOrSlug}` 
      : `filters[post][slug][$eq]=${businessIdOrSlug}`;
    
    const start = (page - 1) * limit;
      
    const response = await fetch(`https://panel.imigo.app/api/posts?${queryParam}&filters[is_active]=true&filters[post_type][name]=product&filters[publishedAt][$notNull]=true&populate=picture&populate=nationality&populate=post_type&populate=creator&pagination[start]=${start}&pagination[limit]=${limit}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching business products:", error);
    return { data: [], meta: { pagination: { total: 0, page: 1, pageSize: limit, pageCount: 0 } } };
  }
};

// Helper function to determine if content should be RTL
const isRTL = (nationality) => {
  if (!nationality || !nationality.data || !nationality.data.attributes) return false;
  // Prefer explicit is_rtl boolean if available
  if (typeof nationality.data.attributes.is_rtl === 'boolean') {
    return nationality.data.attributes.is_rtl;
  }
  // Fallback to language_query
  const rtlLangs = ['ar', 'fa', 'he', 'ur', 'ps', 'dv', 'ku', 'yi'];
  const lang = nationality.data.attributes.language_query?.toLowerCase();
  return rtlLangs.includes(lang);
};

const PostDetailPage = () => {
  const { slug, id } = useParams()
  const location = useLocation()
  const { getLocalizedPath, t } = useLanguage()
  const idOrSlug = slug || id
  const routeSegment = inferRouteSegmentFromPath(location.pathname)
  const [post, setPost] = useState(null)
  const [relatedPosts, setRelatedPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [products, setProducts] = useState([])
  const [productsPagination, setProductsPagination] = useState({
    page: 1,
    limit: 8,
    total: 0,
    totalPages: 0
  })
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [isRtl, setIsRtl] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  useEffect(() => {
    const fetchPostData = async () => {
      try {
        setLoading(true)
        
        // Fetch post by ID or slug
        const response = await fetchPostById(idOrSlug)
        
        if (!response || !response.data) {
          throw new Error('Post not found');
        }
        
        setPost(response.data)
        
        // Fetch related posts of the same type
        if (response.data?.attributes?.post_type) {
          const postTypeName = resolvePostTypeName(response.data.attributes.post_type, routeSegment)
          const relatedResponse = await fetchPostsByType(postTypeName, {
            limit: 4,
            filters: {
              'filters[id][$ne]': response.data.id // Exclude current post
            }
          })
          setRelatedPosts(relatedResponse.data || [])
        }
        
        // Products will be fetched by the products pagination useEffect
        
        // Check if the post has RTL nationality
        if (response.data?.attributes?.nationality) {
          setIsRtl(isRTL(response.data.attributes.nationality));
        }
        
        setLoading(false)
      } catch (err) {
        console.error('Error fetching post details:', err)
        setError('Post not found or unavailable. Please check the URL and try again.')
        setLoading(false)
      }
    }
    
    fetchPostData()
    
    window.scrollTo(0, 0)
  }, [idOrSlug, routeSegment])

  // Fetch products when pagination changes
  useEffect(() => {
    const fetchProducts = async () => {
      const typeName = resolvePostTypeName(post.attributes.post_type, routeSegment)
      if (typeName.toLowerCase() !== 'business') {
        return
      }
      
      try {
        setLoadingProducts(true);
        const idOrSlugParam = slug || id
        const productsResponse = await fetchBusinessProducts(
          idOrSlugParam,
          productsPagination.page, 
          productsPagination.limit
        );
        setProducts(productsResponse.data || []);
        const pagination = productsResponse.meta?.pagination || {};
        setProductsPagination(prev => ({
          ...prev,
          total: pagination.total || 0,
          totalPages: Math.ceil((pagination.total || 0) / prev.limit)
        }));
        setLoadingProducts(false);
        
        // Scroll to products section when page changes (but not on initial load)
        if (productsPagination.page > 1) {
          const productsSection = document.querySelector('.business-products');
          if (productsSection) {
            productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setLoadingProducts(false);
      }
    };
    
    fetchProducts();
  }, [productsPagination.page, idOrSlug, post])

  const handleProductsPageChange = (pageNumber) => {
    setProductsPagination(prev => ({
      ...prev,
      page: pageNumber
    }))
  }

  // Add this useEffect to handle RTL changes
  useEffect(() => {
    // No global direction or body class; handled per section
    return () => {};
  }, [isRtl]);

  if (loading) {
    return (
      <div className="post-detail-loading py-[100px] text-center">
        <Container>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Container>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="post-detail-error py-[100px]">
        <Container>
          <div className="alert alert-danger" role="alert">
            {error || 'Post not found'}
          </div>
          <div className="text-center mt-4">
            <Link to={getLocalizedPath('/')} className="btn btn-primary">
              {t('common.backHome')}
            </Link>
          </div>
        </Container>
      </div>
    )
  }

  const {
    title,
    description,
    createdAt,
    picture,
    gallery,
    post_type,
    price,
    start,
    end,
    is_imigo_partner,
    map,
    creator
  } = post.attributes

  const postType = resolvePostTypeName(post_type, routeSegment)
  const postTypeUrl = getPostTypeUrl(post_type || postType)
  const author = resolveCreator(creator)
  const fontClass = getNationalityFontClass(post.attributes.nationality)
  const seoDescription = description
    ? description.replace(/<[^>]+>/g, '').slice(0, 160)
    : t('seo.defaultDescription')
  const seoImage = getImageUrl(picture, 'large')
  const hasGallery = gallery?.data && gallery.data.length > 0
  const hasMap = map && map.lat && map.lon
  const isEvent = postType === 'Event'
  const isBusiness = postType === 'Business'
  const isMarketplace = postType === 'Marketplace'

  const breakpointColumns = {
    default: 3,
    1100: 3,
    700: 2,
    500: 1
  }

  const handleImageError = (e) => {
    e.target.src = 'https://panel.imigo.app/uploads/medium_logo_baf7005f31.jpg'
  }

  return (
    <div className={`post-detail-page ${fontClass} ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <SEO
        title={`${title} | Imigo`}
        description={seoDescription}
        image={seoImage}
        type="article"
        publishedTime={createdAt}
      />

      <Container>
        <Breadcrumb className="mb-3 text-sm">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: getLocalizedPath('/') }}>
            {t('menu.home')}
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: getLocalizedPath(`/${postTypeUrl}`) }}>
            {postType}
          </Breadcrumb.Item>
          <Breadcrumb.Item active className="truncate max-w-[180px] sm:max-w-none">{title}</Breadcrumb.Item>
        </Breadcrumb>

        <div className="detail-hero">
          <div className="detail-hero-image">
            <img
              src={getImageUrl(picture, 'large')}
              alt={title}
              onError={handleImageError}
            />
          </div>

          <div className="detail-hero-body">
            <span className="detail-type-badge">{postType}</span>
            <h1 className={`detail-title ${isRtl ? 'text-right' : 'text-left'}`}>{title}</h1>

            <div className={`flex items-center gap-2.5 mb-3 detail-meta ${isRtl ? 'flex-row-reverse' : ''}`}>
              {author.picture ? (
                <img src={author.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-[#0f8a8d] text-white flex items-center justify-center text-sm font-bold">
                  {author.name.charAt(0)}
                </div>
              )}
              <div className={isRtl ? 'text-right' : 'text-left'}>
                <p className="mb-0 text-[11px] uppercase tracking-wide opacity-70">{t('postDetail.postedBy')}</p>
                <p className="mb-0 font-semibold text-[#14202b]">{author.name}</p>
              </div>
            </div>

            <p className={`detail-meta mb-0 ${isRtl ? 'text-right' : 'text-left'}`}>{formatDate(createdAt)}</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="post-content">
          <div className={`content-panel post-description ${isRtl ? 'text-right' : 'text-left'}`}>
            <div
              className="content"
              dir={isRtl ? 'rtl' : 'ltr'}
              dangerouslySetInnerHTML={{
                __html: isRtl
                  ? description?.replace(/<br>&nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;&nbsp;/g, '</li><li>')
                    .replace(/<p><br>&nbsp;&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;&nbsp;/g, '<p><ul class="list-disc pl-4"><li>')
                    .replace(/<br><br>/g, '</li></ul></p><p>')
                    .replace(/<p/g, `<p dir="rtl" class="text-right"`)
                    .replace(/<ul/g, `<ul dir="rtl" class="text-right list-disc pl-4"`)
                    .replace(/<li/g, `<li dir="rtl" class="text-right"`)
                    .replace(/<div/g, `<div dir="rtl" class="text-right"`)
                    .replace(/<span/g, `<span dir="rtl" class="text-right"`)
                    .replace(/<ol/g, `<ol dir="rtl" class="text-right"`)
                    .replace(/<h1/g, `<h1 dir="rtl" class="text-right"`)
                    .replace(/<h2/g, `<h2 dir="rtl" class="text-right"`)
                    .replace(/<h3/g, `<h3 dir="rtl" class="text-right"`)
                    .replace(/<h4/g, `<h4 dir="rtl" class="text-right"`)
                    .replace(/<h5/g, `<h5 dir="rtl" class="text-right"`)
                    .replace(/<h6/g, `<h6 dir="rtl" class="text-right"`)
                    .replace(/<blockquote/g, `<blockquote dir="rtl" class="text-right"`)
                    .replace(/<pre/g, `<pre dir="rtl" class="text-right"`)
                    .replace(/<code/g, `<code dir="rtl" class="text-right"`)
                    .replace(/<strong/g, `<strong dir="rtl" class="text-right"`)
                    .replace(/<em/g, `<em dir="rtl" class="text-right"`)
                    .replace(/<b/g, `<b dir="rtl" class="text-right"`)
                    .replace(/<i/g, `<i dir="rtl" class="text-right"`)
                    .replace(/<a/g, `<a dir="rtl" class="text-right"`)
                    .replace(/<table/g, `<table dir="rtl" class="text-right"`)
                    .replace(/<tr/g, `<tr dir="rtl" class="text-right"`)
                    .replace(/<td/g, `<td dir="rtl" class="text-right"`)
                    .replace(/<th/g, `<th dir="rtl" class="text-right"`)
                  : description
              }}
            />
          </div>

          {/* Gallery - Masonry Layout */}
          {hasGallery && (
            <div className="post-gallery mb-5">
              <Masonry
                breakpointCols={breakpointColumns}
                className={`flex w-auto ${isRtl ? '-mr-4' : '-ml-4'}`}
                columnClassName={`${isRtl ? 'pr-4' : 'pl-4'} bg-clip-padding`}
              >
                {gallery.data.map((image, index) => (
                  <div 
                    key={index} 
                    className="mb-4 cursor-pointer rounded-lg overflow-hidden"
                    onClick={() => {
                      setLightboxIndex(index)
                      setLightboxOpen(true)
                    }}
                  >
                    <img 
                      src={getImageUrl({ data: image }, 'medium')} 
                      alt={`${title} - Image ${index + 1}`} 
                      className="w-full h-auto hover:opacity-90 transition-opacity duration-300"
                      onError={handleImageError}
                    />
                  </div>
                ))}
              </Masonry>
            </div>
          )}

          {/* Lightbox */}
          {lightboxOpen && (
            <Lightbox
              mainSrc={getImageUrl({ data: gallery.data[lightboxIndex] }, 'large')}
              nextSrc={getImageUrl({ data: gallery.data[(lightboxIndex + 1) % gallery.data.length] }, 'large')}
              prevSrc={getImageUrl({ data: gallery.data[(lightboxIndex + gallery.data.length - 1) % gallery.data.length] }, 'large')}
              onCloseRequest={() => setLightboxOpen(false)}
              onMovePrevRequest={() => setLightboxIndex((lightboxIndex + gallery.data.length - 1) % gallery.data.length)}
              onMoveNextRequest={() => setLightboxIndex((lightboxIndex + 1) % gallery.data.length)}
              imageTitle={`${title} - Image ${lightboxIndex + 1}`}
              imageCaption={`${title} - Image ${lightboxIndex + 1}`}
            />
          )}

          {isEvent && (
            <div className="content-panel event-details">
              <div className={`text-sm space-y-2 ${isRtl ? 'text-right' : 'text-left'}`}>
                <div><strong>Start:</strong> {start ? new Date(start).toLocaleString() : 'Not specified'}</div>
                <div><strong>End:</strong> {end ? new Date(end).toLocaleString() : 'Not specified'}</div>
                {price && <div><strong>Price:</strong> {price} OMR</div>}
              </div>
            </div>
          )}

          {isMarketplace && price && (
            <div className="content-panel marketplace-details">
              <div className={`text-sm ${isRtl ? 'text-right' : 'text-left'}`}>
                <strong>Price:</strong> {price} OMR
              </div>
            </div>
          )}

          {hasMap && (
            <div className="content-panel post-map">
              <div className="map-container rounded-xl overflow-hidden h-[220px] md:h-[280px]">
                <iframe
                  title="Location Map"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src={`https://maps.google.com/maps?q=${map.lat},${map.lon}&z=15&output=embed`}
                ></iframe>
              </div>
            </div>
          )}

          {isBusiness && (
            <div className="business-products">
              <h3 className={`section-heading ${isRtl ? 'text-right' : 'text-left'}`}>Products</h3>
              {loadingProducts ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-[#0f8a8d]" role="status">
                    <span className="visually-hidden">Loading products...</span>
                  </div>
                </div>
              ) : products.length > 0 ? (
                <>
                  <Row className="imigo-post-grid g-2">
                    {products.map((product, index) => (
                      <Col key={product.id || index} xs={6} md={4} lg={3}>
                        <div className="product-card">
                          <div className="product-image">
                            <img
                              src={getImageUrl(product.attributes.picture, 'medium')}
                              alt={product.attributes.title}
                              onError={handleImageError}
                              loading="lazy"
                            />
                          </div>
                          <div className="p-2.5">
                            <h4 className={`product-title ${isRtl ? 'text-right' : 'text-left'}`}>
                              {product.attributes.title}
                            </h4>
                            {product.attributes.price && (
                              <div className={`product-price ${isRtl ? 'text-right' : 'text-left'}`}>
                                {product.attributes.price} OMR
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  
                  {/* Products Pagination */}
                  {productsPagination.totalPages > 1 && (
                    <div className="pagination-wrapper mt-4 flex justify-center">
                      <BootstrapPagination>
                        <BootstrapPagination.First
                          onClick={() => handleProductsPageChange(1)}
                          disabled={productsPagination.page === 1}
                        />
                        <BootstrapPagination.Prev
                          onClick={() => handleProductsPageChange(productsPagination.page - 1)}
                          disabled={productsPagination.page === 1}
                        />
                        
                        {[...Array(productsPagination.totalPages)].map((_, index) => {
                          const pageNumber = index + 1
                          // Show current page, and 2 pages before and after
                          if (
                            pageNumber === 1 ||
                            pageNumber === productsPagination.totalPages ||
                            (pageNumber >= productsPagination.page - 2 && pageNumber <= productsPagination.page + 2)
                          ) {
                            return (
                              <BootstrapPagination.Item
                                key={pageNumber}
                                active={pageNumber === productsPagination.page}
                                onClick={() => handleProductsPageChange(pageNumber)}
                              >
                                {pageNumber}
                              </BootstrapPagination.Item>
                            )
                          }
                          // Show ellipsis for skipped pages
                          else if (
                            pageNumber === 2 ||
                            pageNumber === productsPagination.totalPages - 1
                          ) {
                            return <BootstrapPagination.Ellipsis key={pageNumber} />
                          }
                          return null
                        })}
                        
                        <BootstrapPagination.Next
                          onClick={() => handleProductsPageChange(productsPagination.page + 1)}
                          disabled={productsPagination.page === productsPagination.totalPages}
                        />
                        <BootstrapPagination.Last
                          onClick={() => handleProductsPageChange(productsPagination.totalPages)}
                          disabled={productsPagination.page === productsPagination.totalPages}
                        />
                      </BootstrapPagination>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-600">No products available for this business.</p>
                </div>
              )}
            </div>
          )}

          {relatedPosts.length > 0 && (
            <div className="related-posts">
              <h3 className={`section-heading ${isRtl ? 'text-right' : 'text-left'}`}>
                Related {postType}s
              </h3>
              <Row className="imigo-post-grid g-3">
                {relatedPosts.slice(0, 4).map(relatedPost => (
                  <Col key={relatedPost.id} xs={6} md={4} lg={3}>
                    <PostCard post={relatedPost} postType={postType} routeSegment={postTypeUrl} />
                  </Col>
                ))}
              </Row>
              {relatedPosts.length > 4 && (
                <div className="text-center mt-3">
                  <Link
                    to={getLocalizedPath(`/${postTypeUrl}`)}
                    className="inline-block px-4 py-2 rounded-xl text-sm font-semibold text-white no-underline"
                    style={{ background: 'var(--imigo-grad-brand)' }}
                  >
                    View all {postType}s
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* App Download Prompt */}
          <InstallAppCTA
            className="mt-10"
            title={t('appCta.detailTitle')}
            description={t('appCta.detailDescription')}
          />
        </div>
      </Container>
    </div>
  )
}

export default PostDetailPage